import React, { useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';

import { RootState } from '~/redux/store';

import './BuildingsListContainer.css';
import { Roles } from '~/enums/Roles';
import { LayoutView } from '~/components/layout';
import { Layout } from '~/types/layout';
import { getLayout } from '~/api/layout';
import { AssetManagementTable } from '~/components/AssetMangementTable/AssetMangementTable';
import { AssetGoogleReviewsTable } from '~/components/AssetGoogleReviewsTable/AssetGoogleReviewsTable';
import { OwnerGroupPortfolioSummary } from '~/components/OwnerGroupPortfolioSummary/OwnerGroupPortfolioSummary';
import { FreeBuildingsSummaryTable } from '~/components/FreeBuildingsSummaryTable/FreeBuildingsSummaryTable';
import CobuSwitcher from '~/components/CobuSwitcher/CobuSwitcher';
import { BuildingList } from './BuildingList';
import Spacer from '~/components/Spacer/Spacer';
import Axios from 'axios';
import { useOwnerGroupAsssets } from '~/hooks/assets/useAssetManagement';
import { AssetAccessors, IOwnerGroup } from '~/types/assets';

type Props = ReturnType<typeof mapStateToProps>;

const BuildingsListContainer = (props: Props) => {
  const [layout, setLayout] = useState<Layout>();
  const [selectedTab, setSelectedTab] = useState(0);

  const role = useSelector((state: RootState) => {
    return state.auth.userRole;
  });

  // The container only uses the fetched assets to derive owner groups for the
  // Roles.Pm single-owner-group summary. For every other role the result is
  // unused, so skip the (heavy) /assets/overview call there — AssetManagementTable
  // still fetches its own copy when the Asset Management tab is shown.
  const { ownerGroupWiseAssets } = useOwnerGroupAsssets(
    undefined,
    undefined,
    role !== Roles.Pm
  );

  const uniqueOwnerGroups = useMemo(() => {
    const seen = new Map<string, IOwnerGroup>();
    (ownerGroupWiseAssets || []).forEach((asset) => {
      const group = asset[AssetAccessors.ownerGroup];
      if (group?.uuid && !seen.has(group.uuid)) {
        seen.set(group.uuid, group);
      }
    });
    return Array.from(seen.values());
  }, [ownerGroupWiseAssets]);

  const singleOwnerGroup =
    uniqueOwnerGroups.length === 1 ? uniqueOwnerGroups[0] : null;

  const getHomeLayout = () => {
    getLayout('home').then((res) => setLayout(res.data.layout));
  };

  useEffect(() => {
    getHomeLayout();
  }, []);

  return (
    <div>
      <div className='buildinglist-container'>
        {(props.userRole === Roles.Superadmin ||
          props.userRole === Roles.Vcm) && (
          <div>
            <p className='buildinglist-title margin-bottom-32'>
              Welcome to Cobu
            </p>
          </div>
        )}
        {props.userRole === Roles.PM_FREE ? (
          <CobuSwitcher
            optionsLabels={['Building Listing', 'Engagement Summary']}
            content={[<BuildingList />, <FreeBuildingsSummaryTable />]}
            defaultSelected={1}
            onClickCallback={setSelectedTab}
          />
        ) : (
          <>
            {layout && <LayoutView layout={layout} />}
            <Spacer height={20} />
            {(props.userRole === Roles.Vcm ||
              props.userRole === Roles.Pm ||
              props.userRole === Roles.Superadmin) && (
              <>
                {props.userRole === Roles.Pm && singleOwnerGroup && (
                  <>
                    <OwnerGroupPortfolioSummary
                      ownerGroupId={singleOwnerGroup.uuid}
                      ownerGroupName={singleOwnerGroup.name}
                    />
                    <Spacer height={20} />
                  </>
                )}
                <CobuSwitcher
                  optionsLabels={[
                    'Asset Management',
                    'Building Listing',
                    'Engagement Summary'
                  ]}
                  content={[
                    <>
                      <AssetManagementTable
                        showSearchHeader
                        heading='View a Property'
                      />
                      {props.userRole === Roles.Pm && selectedTab === 0 && (
                        <>
                          <Spacer height={20} />
                          <AssetGoogleReviewsTable
                            heading='Google Reviews'
                            pmScopeNoParams
                          />
                        </>
                      )}
                    </>,
                    <BuildingList />,
                    <FreeBuildingsSummaryTable />
                  ]}
                  onClickCallback={setSelectedTab}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function mapStateToProps(state: RootState) {
  return {
    userRole: state.auth.userRole
  };
}

export default connect(mapStateToProps)(BuildingsListContainer);
